<template>
  <!--
    The `ui-card` class is a non-Tailwind hook so non-scoped consumers
    (e.g. Chart.vue's print-width auto-calc) can find UiCard via
    `closest('.ui-card')` without depending on a specific Tailwind
    utility class.
  -->
  <div class="ui-card tw-bg-white tw-border tw-border-gray-200 tw-rounded-md tw-shadow-sm tw-flex tw-flex-col tw-min-w-0">
    <UiCardHeader v-if="$slots.header || header">
      <!-- Slot wins; the prop is the fallback. Mirrors BVN's b-card,
           which supports both `<UiCard header="Title">` and
           `<UiCard><template #header>…rich content…</template></UiCard>`. -->
      <slot name="header">
        {{ header }}
      </slot>
    </UiCardHeader>
    <UiCardBody v-if="!noBody">
      <slot />
    </UiCardBody>
    <slot v-else />
  </div>
</template>

<script setup lang="ts">
  /**
   * UiCard — Tailwind primitive replacing BootstrapVue-Next's `b-card`.
   *
   * Two composition modes (matching BVN behaviour so swap-in is mechanical):
   *
   * 1. Auto-body (default): default slot is wrapped in <UiCardBody>.
   *      <UiCard header="Title">content</UiCard>
   *
   *    The header can be a plain string (`header` prop) OR rich content
   *    (`#header` slot). The slot wins when both are present.
   *      <UiCard>
   *        <template #header><div class="tw-flex tw-justify-between">…</div></template>
   *        body content
   *      </UiCard>
   *
   * 2. Manual (`no-body`): caller composes <UiCardHeader>/<UiCardBody>/etc.
   *      <UiCard no-body>
   *        <UiCardHeader>...</UiCardHeader>
   *        <UiCardBody>...</UiCardBody>
   *      </UiCard>
   */
  import UiCardHeader from './UiCardHeader.vue'
  import UiCardBody from './UiCardBody.vue'

  defineOptions({ name: 'UiCard' })

  interface Props {
    /** Convenience prop — auto-renders a UiCardHeader with this text. */
    header?: string
    /** Disable the auto-body wrap. Caller composes children manually. */
    noBody?: boolean
  }

  withDefaults(defineProps<Props>(), {
    header: undefined,
    noBody: false,
  })
</script>
